import { useState, useEffect, useRef, useCallback } from 'react'
import { auth } from '@/firebase'
import { getWsBaseUrl } from '@/config'

export interface AgentSession {
  id: string
  mode: 'ai' | 'queued' | 'human' | 'closed'
  userId?: string
  assignedAgentId?: string
  assignedAgentName?: string
  assignedAt?: string
  queuedAt?: string
  escalationReason?: string
  tags?: string[]
  note?: string
  projectId: string
  projectName?: string
  lastMessage?: string
  lastMessageAt?: string
  unread?: number
}

export interface AgentMessage {
  id: string
  sessionId: string
  role: 'user' | 'assistant' | 'system' | 'agent'
  content: string
  timestamp: string
  agentId?: string
  agentName?: string
  internal?: boolean
}

interface ServerEvent {
  type: 'new_queue' | 'message' | 'session_update' | 'queue_update'
  data: any
}

interface UseAgentWebSocketReturn {
  connected: boolean
  sessions: AgentSession[]
  setSessions: React.Dispatch<React.SetStateAction<AgentSession[]>>
  messages: Map<string, AgentMessage[]>
  sendMessage: (sessionId: string, content: string) => void
  acceptSession: (sessionId: string) => void
  sendTyping: (sessionId: string) => void
  typingSessionIds: Set<string>
}

const MAX_RECONNECT_DELAY = 30000
const BASE_RECONNECT_DELAY = 1000
// Firebase ID tokens expire in 1 hour; reconnect at 50 minutes to refresh
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000

export function useAgentWebSocket(): UseAgentWebSocketReturn {
  const [connected, setConnected] = useState(false)
  const [sessions, setSessions] = useState<AgentSession[]>([])
  const [messages, setMessages] = useState<Map<string, AgentMessage[]>>(new Map())
  const [typingSessionIds, setTypingSessionIds] = useState<Set<string>>(new Set())

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempt = useRef(0)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>()
  const tokenRefreshTimer = useRef<ReturnType<typeof setTimeout>>()
  const typingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const getWsUrl = useCallback(async () => {
    const user = auth.currentUser
    if (!user) return null
    const token = await user.getIdToken()
    const wsBase = getWsBaseUrl()
    return `${wsBase}/ws/agent?token=${token}`
  }, [])

  const handleEvent = useCallback((event: ServerEvent) => {
    switch (event.type) {
      case 'new_queue': {
        const session = event.data.session as AgentSession
        setSessions(prev => {
          if (prev.some(s => s.id === session.id)) return prev
          return [...prev, session]
        })
        break
      }
      case 'message': {
        const { sessionId, message } = event.data as { sessionId: string; message: AgentMessage }
        setMessages(prev => {
          const next = new Map(prev)
          const list = next.get(sessionId) || []
          if (list.some(m => m.id === message.id)) return prev
          next.set(sessionId, [...list, message])
          return next
        })
        // Update session last message
        setSessions(prev =>
          prev.map(s =>
            s.id === sessionId
              ? { ...s, lastMessage: message.content, lastMessageAt: message.timestamp, unread: (s.unread || 0) + 1 }
              : s
          )
        )
        // Clear typing for this session
        setTypingSessionIds(prev => {
          const next = new Set(prev)
          next.delete(sessionId)
          return next
        })
        break
      }
      case 'session_update': {
        const session = event.data.session as AgentSession
        setSessions(prev => {
          const idx = prev.findIndex(s => s.id === session.id)
          if (idx >= 0) {
            const next = [...prev]
            next[idx] = { ...next[idx], ...session }
            return next
          }
          return [...prev, session]
        })
        break
      }
      case 'queue_update': {
        // Just a count update, sessions list handles display
        break
      }
    }
  }, [])

  const connect = useCallback(async () => {
    const url = await getWsUrl()
    if (!url) return

    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      setConnected(true)
      reconnectAttempt.current = 0
      // Schedule proactive reconnect before token expires
      if (tokenRefreshTimer.current) clearTimeout(tokenRefreshTimer.current)
      tokenRefreshTimer.current = setTimeout(() => {
        // Gracefully close and reconnect with fresh token
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.close() // onclose will trigger reconnect with new token
        }
      }, TOKEN_REFRESH_INTERVAL)
    }

    ws.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data) as ServerEvent
        handleEvent(event)
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      setConnected(false)
      wsRef.current = null
      // Exponential backoff reconnect
      const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempt.current), MAX_RECONNECT_DELAY)
      reconnectAttempt.current++
      reconnectTimer.current = setTimeout(connect, delay)
    }

    ws.onerror = () => {
      ws.close()
    }
  }, [getWsUrl, handleEvent])

  useEffect(() => {
    connect()
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      if (tokenRefreshTimer.current) clearTimeout(tokenRefreshTimer.current)
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
      }
      typingTimers.current.forEach(t => clearTimeout(t))
    }
  }, [connect])

  const sendMessage = useCallback((sessionId: string, content: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'send_message',
        data: { sessionId, content }
      }))
    }
  }, [])

  const acceptSession = useCallback((sessionId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'accept_session',
        data: { sessionId }
      }))
    }
  }, [])

  const sendTyping = useCallback((sessionId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        data: { sessionId }
      }))
    }
  }, [])

  return {
    connected,
    sessions,
    setSessions,
    messages,
    sendMessage,
    acceptSession,
    sendTyping,
    typingSessionIds,
  }
}

export default useAgentWebSocket
